import {ApiClient} from '@hconnect/apiclient'
import {
  EventSearchDto,
  EventSearchCriteria,
  ShiftEventDto,
  EventType,
  EventStatus,
  StatusListWithApproverStates,
  Iso8601,
  ShiftEventsSortBy,
  SortOrder
} from '@hconnect/common/types'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

const ITEMS_PER_PAGE = 100

export const QueryKey = 'events-search-infinite'

type UseEventsSearchInfiniteParams = {
  plantId: string
  type: EventType
  from: Iso8601
  to: Iso8601
  statuses?: EventStatus<StatusListWithApproverStates>[]
  sortBy?: ShiftEventsSortBy
  sortOrder?: SortOrder
}

type UseEventsSearchInfiniteOptions = Omit<
  UseInfiniteQueryOptions<EventSearchDto, AxiosError, ShiftEventDto[]>,
  'queryKey' | 'queryFn' | 'select'
>

export const useEventsSearchInfinite = (
  {
    plantId,
    type,
    from,
    to,
    statuses,
    sortBy = ShiftEventsSortBy.Created,
    sortOrder = SortOrder.Descending
  }: UseEventsSearchInfiniteParams,
  options?: UseEventsSearchInfiniteOptions
) => {
  const payload: EventSearchCriteria = {
    timeRange: {
      startDate: from,
      endDate: to
    },
    eventType: [type],
    status: statuses,
    sortBy,
    sortOrder
  }

  return useInfiniteQuery({
    queryKey: [QueryKey, plantId, type, from, to, statuses, sortBy, sortOrder],
    queryFn: async ({pageParam = 0, signal}) => {
      const offset = pageParam as number
      const response = await ApiClient.axiosInstance.post<EventSearchDto>(
        `/shifthandover/${plantId}/search/events`,
        {
          ...payload,
          pageNumber: offset / ITEMS_PER_PAGE,
          itemsPerPage: ITEMS_PER_PAGE
        },
        {
          signal
        }
      )

      return response.data
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.events.length === ITEMS_PER_PAGE ? pages.length * ITEMS_PER_PAGE : undefined
    },
    initialPageParam: 0,
    select: (data) => ({...data, pages: data.pages.map((page) => page.events)}),
    ...options
  })
}
